import { Link } from 'react-router-dom';
import { Banner, Timestamp } from '@/components/ds-zta';
import { useLicenseState } from '../hooks/useLicenseState';
import { usePermissions } from '../hooks/usePermissions';
import { licenseAlert } from '@/lib/license';

/**
 * The licence, said once above every page (design eval F25). When it lapses
 * the API starts refusing calls, and without this a user sees only failures.
 * Shown to everyone; the link to Settings › License only to an admin, who is
 * the one who can act on it.
 */
export default function LicenseBanner() {
  const { data } = useLicenseState();
  const { isAdmin } = usePermissions();
  const alert = licenseAlert(data);
  if (!alert) return null;

  const action = isAdmin() ? (
    <Link to="/settings?tab=license" className="text-sm font-medium underline underline-offset-2">
      Open License settings
    </Link>
  ) : undefined;

  if (alert.kind === 'expiring') {
    return (
      <Banner
        variant="warning"
        className="mb-4"
        title={alert.days === 0 ? 'The license expires today' : `The license expires in ${alert.days} day${alert.days === 1 ? '' : 's'}`}
        action={action}
      >
        Access to resources and sessions stops on <Timestamp value={alert.endDate} noSeconds />
        {isAdmin() ? ' unless it is renewed.' : '. Ask an administrator to renew it.'}
      </Banner>
    );
  }

  const reason =
    alert.status === 'expired'
      ? 'The license has expired.'
      : alert.status === 'unlicensed'
        ? 'This installation has no active license.'
        : 'The license could not be verified.';
  return (
    <Banner variant="danger" className="mb-4" title="Access to this product is suspended" action={action}>
      {reason} Resources, sessions and settings are unavailable until it is {alert.status === 'unlicensed' ? 'activated' : 'renewed'}
      {isAdmin() ? '.' : '. Ask an administrator.'} Your data is untouched.
    </Banner>
  );
}
